/**
 * Left rail of the omnichannel inbox: search, channel + status filters and
 * the conversation list, ordered by latest activity.
 */
import { useMemo } from 'react';
import { MessagesSquare, Search, Wifi, WifiOff } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import type { CrmConversation } from '@/api/bi/crmConversations';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import { CHANNEL_META, ContactAvatar, contactLabel } from './channelMeta';

const STATUS_TABS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'awaiting_agent', label: 'Needs agent' },
  { value: 'bot_handling', label: 'AI' },
  { value: 'agent_active', label: 'Mine' },
  { value: 'closed', label: 'Closed' },
];

export interface InboxFilters {
  q: string;
  channel: string;
  status: string;
}

export function ConversationListPane({
  conversations,
  isLoading,
  isError,
  onRetry,
  selectedId,
  onSelect,
  filters,
  onFilters,
  connected,
}: {
  conversations: CrmConversation[];
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  selectedId: number | null;
  onSelect: (id: number) => void;
  filters: InboxFilters;
  onFilters: (next: InboxFilters) => void;
  connected: boolean;
}) {
  const needsAgentCount = useMemo(
    () => conversations.filter((c) => c.status === 'awaiting_agent').length,
    [conversations],
  );

  const visible = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return conversations
      .filter((c) => !filters.channel || c.channel === filters.channel)
      .filter((c) => !filters.status || c.status === filters.status)
      .filter((c) => {
        if (!q) return true;
        const hay = [
          c.contact?.display_name,
          c.contact?.email,
          c.contact?.phone,
          c.subject,
          String(c.id),
          c.order_id ? String(c.order_id) : '',
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return tb - ta;
      });
  }, [conversations, filters]);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border border-border bg-surface">
      {/* Search + live indicator */}
      <div className="flex items-center gap-2 border-b border-border p-2.5">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-2 text-xs outline-none focus:border-primary/50"
            placeholder="Search name, phone, order #…"
            value={filters.q}
            onChange={(e) => onFilters({ ...filters, q: e.target.value })}
          />
        </div>
        <span
          title={connected ? 'Live updates connected' : 'Live updates reconnecting…'}
          className={cn('shrink-0', connected ? 'text-success' : 'text-muted-foreground')}
        >
          {connected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
        </span>
      </div>

      {/* Channel chips */}
      <div className="flex flex-wrap items-center gap-1 border-b border-border p-2">
        <ChannelChip
          active={filters.channel === ''}
          label="All"
          onClick={() => onFilters({ ...filters, channel: '' })}
        />
        {Object.entries(CHANNEL_META).map(([key, meta]) => {
          const Icon = meta.icon;
          return (
            <ChannelChip
              key={key}
              active={filters.channel === key}
              label={meta.label}
              icon={<Icon className={cn('h-3 w-3', meta.fg)} />}
              onClick={() => onFilters({ ...filters, channel: filters.channel === key ? '' : key })}
            />
          );
        })}
      </div>

      {/* Status tabs */}
      <div className="flex items-center gap-1 border-b border-border p-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => onFilters({ ...filters, status: tab.value })}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors',
              filters.status === tab.value
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted',
            )}
          >
            {tab.label}
            {tab.value === 'awaiting_agent' && needsAgentCount > 0 ? (
              <span
                className={cn(
                  'rounded-full px-1 text-[10px] font-semibold',
                  filters.status === tab.value
                    ? 'bg-primary-foreground/20'
                    : 'bg-warning/20 text-warning-soft',
                )}
              >
                {needsAgentCount}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : isError ? (
          // An empty inbox and an unreachable inbox look the same to an agent
          // waiting for work, so they must not render the same.
          <div className="p-4">
            <ErrorState description="Could not load conversations." onRetry={onRetry} />
          </div>
        ) : visible.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<MessagesSquare className="h-5 w-5" />}
              title="No conversations"
              description={
                filters.q || filters.channel || filters.status
                  ? 'Nothing matches the current filters.'
                  : 'Inbound Gmail and Meta messages appear here once channels are connected.'
              }
            />
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {visible.map((c) => (
              <ConversationRow
                key={c.id}
                conversation={c}
                active={c.id === selectedId}
                onClick={() => onSelect(c.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ChannelChip({
  active,
  label,
  icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ring-1 ring-inset transition-colors',
        active
          ? 'bg-primary/10 text-primary ring-primary/40'
          : 'bg-background text-muted-foreground ring-border hover:bg-muted',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function ConversationRow({
  conversation: c,
  active,
  onClick,
}: {
  conversation: CrmConversation;
  active: boolean;
  onClick: () => void;
}) {
  const name = contactLabel(c.contact);
  const unread = c.unread_count > 0;
  const hasDraft = Boolean(c.pending_draft?.draft);
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors',
          active ? 'bg-primary/5 shadow-[inset_2px_0_0] shadow-primary' : 'hover:bg-muted/40',
        )}
      >
        <ContactAvatar name={name} channel={c.channel} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className={cn('truncate text-[13px]', unread ? 'font-bold' : 'font-medium')}>
              {name}
            </span>
            <span className="shrink-0 text-[10px] text-muted-foreground">
              {c.last_message_at ? fmt.ago(c.last_message_at) : ''}
            </span>
          </div>
          <p
            className={cn(
              'mt-0.5 truncate text-[11px]',
              unread ? 'font-semibold text-foreground' : 'text-muted-foreground',
            )}
          >
            {c.subject || 'No subject'}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {c.status === 'awaiting_agent' ? <Badge tone="warning">Needs agent</Badge> : null}
            {hasDraft ? <Badge tone="info">AI draft</Badge> : null}
            {c.contact?.user_id ? <Badge tone="success">Customer</Badge> : null}
            {c.order_id ? <Badge tone="primary">#{c.order_id}</Badge> : null}
            {unread ? (
              <span className="ml-auto inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                {c.unread_count}
              </span>
            ) : null}
          </div>
        </div>
      </button>
    </li>
  );
}
